class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.menuTriggerMobile = this.querySelector('.header__menu-trigger.--mobile');
    this.menuTriggerDesktop = this.querySelector('.header__menu-trigger.--desktop');
    this.cartButton = this.querySelectorAll('.header__cart');
    this.cartBadges = this.querySelectorAll('.header__cart .item-value');
    this.searchButton = this.querySelector('.header__search');
    this.navMenu = this.querySelector('.header__menu');
    this.megaHelp = this.querySelector('.header--mega-help');
    this.menuCloseTimeout = null;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onSearchClosed = this.onSearchClosed.bind(this);
    this.onMenuEnter = this.onMenuEnter.bind(this);
    this.onMenuLeave = this.onMenuLeave.bind(this);

    if (this.menuTriggerMobile) {
      this.menuTriggerMobile.addEventListener('click', this.toggleMenuMobile.bind(this));
    }
    if (this.menuTriggerDesktop) {
      this.menuTriggerDesktop.addEventListener('click', this.toggleMenuDesktop.bind(this));
    }
    if (this.cartButton) {
      this.cartButton.forEach(button => {
        button.addEventListener('click', this.openCartDrawer.bind(this));
      });
    }
    if (this.searchButton) {
      this.searchButton.addEventListener('click', this.openSearch.bind(this));
    }
  }

  connectedCallback() {
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('search:closed', this.onSearchClosed);

    if (this.menuTriggerDesktop) {
      this.menuTriggerDesktop.addEventListener('mouseenter', this.onMenuEnter);
    }
    if (this.megaHelp) {
      this.megaHelp.addEventListener('mouseenter', this.onMenuEnter);
    }
    if (this.navMenu) {
      this.navMenu.addEventListener('mouseleave', this.onMenuLeave);
    }

    // Set initial header height and bottom position
    this.updateHeaderHeight();
    this.setupBannerObserver();

    // Update on resize
    this.resizeObserver = new ResizeObserver(() => {
      this.updateHeaderHeight();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('search:closed', this.onSearchClosed);

    if (this.menuTriggerDesktop) {
      this.menuTriggerDesktop.removeEventListener('mouseenter', this.onMenuEnter);
    }
    if (this.megaHelp) {
      this.megaHelp.removeEventListener('mouseenter', this.onMenuEnter);
    }
    if (this.navMenu) {
      this.navMenu.removeEventListener('mouseleave', this.onMenuLeave);
    }
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
    }

    if (this.bannerObserver) {
      this.bannerObserver.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  get isHoverDesktop() {
    return window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches;
  }

  onMenuEnter() {
    if (!this.isHoverDesktop) return;

    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
      this.menuCloseTimeout = null;
    }

    if (this.classList.contains('header--menu-open')) return;
    if (this.classList.contains('header--search-open')) return;
    if (this.classList.contains('header--drawer-open')) return;

    this.closeCartDrawer();
    this.classList.add('header--menu-open');
    this.menuTriggerDesktop?.setAttribute('aria-expanded', 'true');
  }

  onMenuLeave() {
    if (!this.isHoverDesktop) return;

    this.menuCloseTimeout = window.setTimeout(() => {
      this.menuCloseTimeout = null;
      this.closeAll();
    }, 150);
  }

  /**
   * Updates the cart badge when cart is updated
   * @param {CustomEvent} event - The cart update event
   */
  onCartUpdate(event) {
    if (!this.cartBadges) return;

    const itemCount = event.detail?.data?.itemCount;
    if (typeof itemCount !== 'number') return;

    // Update badge text - always show at least [0]
    this.cartBadges.forEach((badge) => {
      badge.textContent = `[${itemCount}]`;
    });
  }

  updateHeaderHeight() {
    const height = this.offsetHeight;
    document.body.style.setProperty('--header-height', `${height}px`);
  }

  setupBannerObserver() {
    const banner = document.querySelector('announcement-banner');

    if (!banner) {
      document.body.style.setProperty('--header-bottom', `${this.offsetHeight}px`);
      return;
    }

    const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);

    this.bannerObserver = new IntersectionObserver(
      ([entry]) => {
        const visibleBannerHeight = entry.intersectionRect.height;
        document.body.style.setProperty('--header-bottom', `${visibleBannerHeight + this.offsetHeight}px`);
      },
      { threshold: thresholds }
    );

    this.bannerObserver.observe(banner);
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.code === 'Escape') {
      const isOpen = this.classList.contains('header--menu-open');

      if (isOpen) {
        this.closeAll();
        const trigger = this.menuTriggerMobile?.offsetParent ? this.menuTriggerMobile : this.menuTriggerDesktop;
        trigger?.focus();
      }
    }
  }

  /** @param {PointerEvent} event */
  onDocumentPointerDown(event) {
    const isMenuOpen = this.classList.contains('header--menu-open');
    const isSearchOpen = this.classList.contains('header--search-open');

    if (!isMenuOpen && !isSearchOpen) return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    // Keep menu open when interacting within the header component.
    if (this.contains(target)) return;

    if (isMenuOpen) this.closeAll();
    if (isSearchOpen) this.closeSearch();
  }

  closeAll() {
    /** @type {(HTMLElement & { close?: () => void }) | null} */
    const megaMenu = this.querySelector('mega-menu-component');
    this.classList.remove('header--menu-open');

    if (this.menuTriggerMobile) this.menuTriggerMobile.setAttribute('aria-expanded', 'false');
    if (this.menuTriggerDesktop) this.menuTriggerDesktop.setAttribute('aria-expanded', 'false');

    megaMenu?.close?.();
  }

  toggleMenuMobile() {
    const isOpen = this.classList.contains('header--menu-open');

    if (isOpen) {
      this.closeAll();
    } else {
      // Fermer le drawer cart si ouvert
      this.closeCartDrawer();

      this.classList.add('header--menu-open');
      this.menuTriggerMobile?.setAttribute('aria-expanded', 'true');
    }
  }

  toggleMenuDesktop() {
    const isOpen = this.classList.contains('header--menu-open');

    if (isOpen) {
      this.closeAll();
    } else {
      // Fermer le drawer cart si ouvert
      this.closeCartDrawer();

      this.classList.add('header--menu-open');
      this.menuTriggerDesktop?.setAttribute('aria-expanded', 'true');
    }
  }

  openCartDrawer() {
    const cartDrawerElement = document.querySelector('cart-drawer');
    const cartDialog = /** @type {HTMLDialogElement | null} */ (
      cartDrawerElement?.querySelector('dialog')
    );
    if (cartDialog instanceof HTMLDialogElement) {
      // Toggle: si le drawer est déjà ouvert, le fermer
      if (cartDialog.hasAttribute('open')) {
        cartDialog.close();
        this.classList.remove('header--drawer-open');
      } else {
        // Fermer le menu si ouvert
        this.closeAll();

        cartDialog.show();
        this.classList.add('header--drawer-open');
      }
    }
  }

  closeCartDrawer() {
    const cartDrawerElement = document.querySelector('cart-drawer');
    const cartDialog = /** @type {HTMLDialogElement | null} */ (
      cartDrawerElement?.querySelector('dialog')
    );
    if (cartDialog instanceof HTMLDialogElement && cartDialog.hasAttribute('open')) {
      cartDialog.close();
      this.classList.remove('header--drawer-open');
    }
  }

  openSearch() {
    this.closeAll();
    this.closeCartDrawer();
    this.classList.add('header--search-open');
    this.searchButton?.setAttribute('aria-expanded', 'true');
    document.dispatchEvent(new CustomEvent('search:open'));

    // Focus input after transition
    setTimeout(() => {
      const input = this.querySelector('input[type="search"]');
      if (input) input.focus();
    }, 100);
  }

  closeSearch() {
    this.classList.remove('header--search-open');
    this.searchButton?.setAttribute('aria-expanded', 'false');
    document.dispatchEvent(new CustomEvent('search:close'));
  }

  onSearchClosed() {
    this.classList.remove('header--search-open');
    this.searchButton?.setAttribute('aria-expanded', 'false');
    this.searchButton?.focus();
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}